import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Menu, MenuItem, Popover, Box, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const SelectorButton = styled(Button)`
  text-transform: none;
  font-weight: 500;
  color: #333;
  background-color: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 16px;
  &:hover {
    background-color: #eaeaea;
  }
`;

const DateRangeText = styled.span`
  font-size: 14px;
`;

export type Period = 'This week' | 'This month' | 'This year' | 'Custom Range';

interface DatePeriodSelectorProps {
    onDateRangeChange: (from: Date, to: Date, period: Period) => void;
    initialPeriod?: Period;
}

const DatePeriodSelector: React.FC<DatePeriodSelectorProps> = ({
    onDateRangeChange,
    initialPeriod = 'This month'
}) => {
    const [selectedPeriod, setSelectedPeriod] = useState<Period>(initialPeriod);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [datePickerAnchorEl, setDatePickerAnchorEl] = useState<null | HTMLElement>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [displayDateRange, setDisplayDateRange] = useState<string>('');

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handlePeriodSelect = (period: Period) => {
        setSelectedPeriod(period);
        handleMenuClose();

        if (period === 'Custom Range') {
            setDatePickerAnchorEl(anchorEl);
        } else {
            // Calculate date range based on selected period
            const now = new Date();
            let fromDate, toDate;

            switch (period) {
                case 'This week':
                    // Manual calculation of week start (Monday) and end (Sunday)
                    const day = now.getDay();
                    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
                    fromDate = new Date(now.setDate(diff));
                    fromDate.setHours(0, 0, 0, 0);

                    toDate = new Date(fromDate);
                    toDate.setDate(toDate.getDate() + 6);
                    toDate.setHours(23, 59, 59, 999);
                    break;

                case 'This month':
                    fromDate = new Date(now.getFullYear(), now.getMonth(), 1);
                    toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    toDate.setHours(23, 59, 59, 999);
                    break;

                case 'This year':
                    fromDate = new Date(now.getFullYear(), 0, 1);
                    toDate = new Date(now.getFullYear(), 11, 31);
                    toDate.setHours(23, 59, 59, 999);
                    break;
            }

            if (fromDate && toDate) {
                setStartDate(fromDate);
                setEndDate(toDate);
                setDisplayDateRange(formatDateRange(fromDate, toDate));
                onDateRangeChange(fromDate, toDate, period);
            }
        }
    };

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date);
        if (date && endDate) {
            if (date > endDate) {
                // If start date is after end date, set end date to start date
                setEndDate(date);
                setDisplayDateRange(formatDateRange(date, date));
                onDateRangeChange(date, date, 'Custom Range');
            } else {
                setDisplayDateRange(formatDateRange(date, endDate));
                onDateRangeChange(date, endDate, 'Custom Range');
            }
        }
    };

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date);
        if (startDate && date) {
            setDisplayDateRange(formatDateRange(startDate, date));
            onDateRangeChange(startDate, date, 'Custom Range');
            // Close date picker if both dates are set
            if (startDate && date) {
                setDatePickerAnchorEl(null);
            }
        }
    };

    const formatDateRange = (start: Date, end: Date): string => {
        // Manual date formatting to avoid date-fns dependency
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const formatDate = (date: Date) => {
            const month = months[date.getMonth()];
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month} ${day}, ${year}`;
        };

        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    // Set initial date range
    useEffect(() => {
        handlePeriodSelect(initialPeriod);
    }, []);

    return (
        <>
            <SelectorButton
                onClick={handleMenuOpen}
                endIcon={<KeyboardArrowDownIcon />}
            >
                {selectedPeriod === 'Custom Range' ? (
                    <DateRangeText>
                        {displayDateRange || 'Select date range'}
                    </DateRangeText>
                ) : (
                    selectedPeriod
                )}
            </SelectorButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handlePeriodSelect('This week')}>This week</MenuItem>
                <MenuItem onClick={() => handlePeriodSelect('This month')}>This month</MenuItem>
                <MenuItem onClick={() => handlePeriodSelect('This year')}>This year</MenuItem>
                <MenuItem onClick={() => handlePeriodSelect('Custom Range')}>Custom Range</MenuItem>
            </Menu>

            <Popover
                open={Boolean(datePickerAnchorEl)}
                anchorEl={datePickerAnchorEl}
                onClose={() => setDatePickerAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2">Select date range</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                minDate={startDate || undefined}
                                slotProps={{ textField: { size: 'small' } }}
                            />
                        </LocalizationProvider>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default DatePeriodSelector;